import { useState, useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * Location text input backed by Mapbox place search. Picking a suggestion
 * sets real lat/lng; free typing without picking one clears them so a
 * stale coordinate never survives an edit to the text.
 */
export default function LocationAutocomplete({ value, onChange, placeholder = 'Location (optional)' }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const skipNextSearch = useRef(false);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }
    if (!MAPBOX_TOKEN || query.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    const handle = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&limit=5`
        );
        const data = await res.json();
        setSuggestions(data.features || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    onChange({ location: text, latitude: null, longitude: null });
  };

  const handleSelect = (feature) => {
    const [longitude, latitude] = feature.center;
    skipNextSearch.current = true;
    setQuery(feature.place_name);
    setSuggestions([]);
    setOpen(false);
    onChange({ location: feature.place_name, latitude, longitude });
  };

  return (
    <div className="relative" ref={containerRef}>
      <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-platinum-400" />
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className="w-full pl-8 pr-3 py-2 border border-platinum-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-400 text-sm"
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-platinum-200 rounded-lg shadow-elevated max-h-48 overflow-y-auto">
          {suggestions.map((feature) => (
            <li key={feature.id}>
              <button
                type="button"
                onClick={() => handleSelect(feature)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-platinum-50 transition-colors"
              >
                {feature.place_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
